import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronUp,
  Search,
  X,
  SlidersHorizontal,
} from 'lucide-react';

import type { PhotoFilters as PhotoFiltersType, PipelineStatus } from '@/api/types/photo.types';

interface PhotoFiltersProps {
  filters: PhotoFiltersType;
  onChange: (filters: PhotoFiltersType) => void;
  isOpen: boolean;
  onToggle: () => void;
}

const PIPELINE_STATUSES: { value: PipelineStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'exif_done', label: 'EXIF Done' },
  { value: 'quality_done', label: 'Quality Done' },
  { value: 'faces_done', label: 'Faces Done' },
  { value: 'caption_done', label: 'Caption Done' },
  { value: 'similarity_done', label: 'Similarity Done' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

/**
 * Collapsible filter panel for the photo gallery.
 * Provides controls for quality range, face count, phase, date range,
 * caption search, boolean toggles, and pipeline status.
 */
export const PhotoFilters = React.memo(function PhotoFilters({
  filters,
  onChange,
  isOpen,
  onToggle,
}: PhotoFiltersProps) {
  const { t } = useTranslation();

  const update = useCallback(
    (partial: Partial<PhotoFiltersType>) => {
      onChange({ ...filters, ...partial });
    },
    [filters, onChange],
  );

  const clearAll = useCallback(() => {
    onChange({ event: filters.event });
  }, [filters.event, onChange]);

  const hasActiveFilters =
    !!filters.search ||
    filters.quality_min != null ||
    filters.quality_max != null ||
    filters.face_count_min != null ||
    filters.face_count_max != null ||
    filters.is_starred != null ||
    filters.is_hidden != null ||
    filters.is_group_best != null ||
    filters.is_blurry != null ||
    !!filters.pipeline_status ||
    !!filters.taken_after ||
    !!filters.taken_before ||
    !!filters.phase;

  return (
    <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      {/* Header / toggle */}
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
      >
        <span className="inline-flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4" />
          {t('actions.filter')}
          {hasActiveFilters && (
            <span className="rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-semibold text-primary-700 dark:bg-primary-900/40 dark:text-primary-400">
              Active
            </span>
          )}
        </span>
        {isOpen ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </button>

      {isOpen && (
        <div className="space-y-4 border-t border-gray-200 px-4 py-4 dark:border-gray-700">
          {/* Search */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
              {t('actions.search')}
            </label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={filters.search ?? ''}
                onChange={(e) => update({ search: e.target.value || undefined })}
                placeholder={t('gallery.searchPlaceholder')}
                className="w-full rounded-md border border-gray-300 bg-white py-1.5 pl-8 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
          </div>

          {/* Quality range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('gallery.qualityMin')}
              </label>
              <input
                type="number"
                min={0}
                max={100}
                value={filters.quality_min ?? ''}
                onChange={(e) =>
                  update({
                    quality_min: e.target.value
                      ? Number(e.target.value)
                      : undefined,
                  })
                }
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                placeholder="0"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('gallery.qualityMax')}
              </label>
              <input
                type="number"
                min={0}
                max={100}
                value={filters.quality_max ?? ''}
                onChange={(e) =>
                  update({
                    quality_max: e.target.value
                      ? Number(e.target.value)
                      : undefined,
                  })
                }
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                placeholder="100"
              />
            </div>
          </div>

          {/* Face count range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('gallery.facesMin')}
              </label>
              <input
                type="number"
                min={0}
                value={filters.face_count_min ?? ''}
                onChange={(e) =>
                  update({
                    face_count_min: e.target.value
                      ? Number(e.target.value)
                      : undefined,
                  })
                }
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                placeholder="0"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('gallery.facesMax')}
              </label>
              <input
                type="number"
                min={0}
                value={filters.face_count_max ?? ''}
                onChange={(e) =>
                  update({
                    face_count_max: e.target.value
                      ? Number(e.target.value)
                      : undefined,
                  })
                }
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                placeholder="Any"
              />
            </div>
          </div>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('gallery.takenAfter')}
              </label>
              <input
                type="datetime-local"
                value={filters.taken_after ?? ''}
                onChange={(e) =>
                  update({ taken_after: e.target.value || undefined })
                }
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('gallery.takenBefore')}
              </label>
              <input
                type="datetime-local"
                value={filters.taken_before ?? ''}
                onChange={(e) =>
                  update({ taken_before: e.target.value || undefined })
                }
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
          </div>

          {/* Pipeline status */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
              {t('gallery.pipelineStatus')}
            </label>
            <select
              value={filters.pipeline_status ?? ''}
              onChange={(e) =>
                update({
                  pipeline_status: (e.target.value as PipelineStatus) || undefined,
                })
              }
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              <option value="">{t('events.allStatuses')}</option>
              {PIPELINE_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          {/* Boolean toggles */}
          <div className="flex flex-wrap gap-2">
            <ToggleChip
              label={t('gallery.starred')}
              active={filters.is_starred === true}
              onClick={() =>
                update({
                  is_starred: filters.is_starred === true ? undefined : true,
                })
              }
            />
            <ToggleChip
              label={t('gallery.hidden')}
              active={filters.is_hidden === true}
              onClick={() =>
                update({
                  is_hidden: filters.is_hidden === true ? undefined : true,
                })
              }
            />
            <ToggleChip
              label={t('gallery.bestPicksOnly')}
              active={filters.is_group_best === true}
              onClick={() =>
                update({
                  is_group_best:
                    filters.is_group_best === true ? undefined : true,
                })
              }
            />
            <ToggleChip
              label={t('gallery.blurry')}
              active={filters.is_blurry === true}
              onClick={() =>
                update({
                  is_blurry: filters.is_blurry === true ? undefined : true,
                })
              }
            />
          </div>

          {/* Clear all */}
          {hasActiveFilters && (
            <button
              onClick={clearAll}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              <X className="h-3 w-3" />
              {t('events.clearFilters')}
            </button>
          )}
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Small toggle chip used for boolean filters
// ---------------------------------------------------------------------------
function ToggleChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
        active
          ? 'border-primary-500 bg-primary-50 text-primary-700 dark:border-primary-600 dark:bg-primary-900/30 dark:text-primary-400'
          : 'border-gray-300 text-gray-600 hover:border-gray-400 dark:border-gray-600 dark:text-gray-400'
      }`}
    >
      {label}
    </button>
  );
}
